import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, Typography, Row, Col, Tag, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, TagsOutlined, BulbOutlined, InfoCircleOutlined, EditOutlined, SoundOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axios from '../components/Axios';

const { TextArea } = Input;
const { Title, Text } = Typography;

// editingAngle — pass the full angle record to open the form in edit mode
// (PUT /api/angle/:angleId). Leave null/omitted for create mode (POST /api/addangle) —
// existing callers (e.g. MainQuora's "Add Angles" button) are unaffected.
const AngleForm = ({ visible, onCancel, userId, onSuccess, editingAngle = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editingAngle;

  // Keywords modal state
  const [keywordsModalVisible, setKeywordsModalVisible] = useState(false);
  const [keywordsText, setKeywordsText] = useState('');
  const [keywords, setKeywords] = useState([]);

  // CTAs state
  const [ctasModalVisible, setCtasModalVisible] = useState(false);
  const [ctaInputs, setCtaInputs] = useState(['']);
  const [ctas, setCtas] = useState([]);

  // Prefill fields when opening in edit mode; reset when opening fresh for create
  useEffect(() => {
    if (!visible) return;
    if (editingAngle) {
      form.setFieldsValue({
        angleName:  editingAngle.angleName || '',
        basicInfo:  editingAngle.basicInfo || '',
        brandVoice: editingAngle.brandVoice || '',
      });
      setKeywords(editingAngle.keywords || []);
      setCtas(editingAngle.ctas || []);
      setKeywordsText((editingAngle.keywords || []).join('\n'));
      setCtaInputs((editingAngle.ctas || []).length ? editingAngle.ctas : ['']);
    } else {
      form.resetFields();
      setKeywords([]);
      setCtas([]);
      setKeywordsText('');
      setCtaInputs(['']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingAngle]);

  // Handle Keywords
  const handleKeywordsSubmit = () => {
    const keywordList = keywordsText
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    setKeywords(keywordList);
    setKeywordsModalVisible(false);
    toast.success(`${keywordList.length} keywords added!`);
  };

  const removeKeyword = (index) => setKeywords(keywords.filter((_, i) => i !== index));

  // Handle CTAs
  const addCtaInput = () => setCtaInputs([...ctaInputs, '']);

  const removeCtaInput = (index) => {
    if (ctaInputs.length > 1) setCtaInputs(ctaInputs.filter((_, i) => i !== index));
  };

  const updateCtaInput = (index, value) => {
    const newInputs = [...ctaInputs];
    newInputs[index] = value;
    setCtaInputs(newInputs);
  };

  const handleCtasSubmit = () => {
    const ctaList = ctaInputs.map(c => c.trim()).filter(c => c.length > 0);
    setCtas(ctaList);
    setCtasModalVisible(false);
    toast.success(`${ctaList.length} CTAs added!`);
  };

  const removeCta = (index) => setCtas(ctas.filter((_, i) => i !== index));

  // Handle main form submission
  const handleSubmit = async (values) => {
    if (keywords.length === 0) { toast.error('Please add at least one keyword'); return; }
    if (ctas.length === 0)     { toast.error('Please add at least one CTA');     return; }

    setLoading(true);
    try {
      if (isEditMode) {
        await axios.put(`/api/angle/${editingAngle._id}`, {
          angleName:  values.angleName,
          keywords,
          ctas,
          basicInfo:  values.basicInfo  || '',
          brandVoice: values.brandVoice || '',
        });
        toast.success('Angle updated successfully!');
      } else {
        await axios.post('/api/addangle', {
          userId,
          angleName:  values.angleName,
          keywords,
          ctas,
          basicInfo:  values.basicInfo  || '',
          brandVoice: values.brandVoice || '',   // NEW — sent to backend
        });
        toast.success('Angle added successfully!');
      }

      form.resetFields();
      setKeywords([]);
      setCtas([]);
      setKeywordsText('');
      setCtaInputs(['']);
      onSuccess?.();
      onCancel();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} angle. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setKeywords([]);
    setCtas([]);
    setKeywordsText('');
    setCtaInputs(['']);
    onCancel();
  };

  return (
    <>
      {/* Main Angle Form Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isEditMode ? <EditOutlined /> : <BulbOutlined />}
            <span>{isEditMode ? 'Edit Angle' : 'Add New Angle'}</span>
          </div>
        }
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '20px' }}>

          {/* Row: Angle Name + Keywords Button + CTAs Button */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>

            <div style={{ flex: 1 }}>
              <Form.Item
                label={<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><EditOutlined /><span>Angle Name</span></div>}
                name="angleName"
                rules={[{ required: true, message: 'Please enter angle name' }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="Enter angle name" size="large" />
              </Form.Item>
            </div>

            <div style={{ flex: 1 }}>
              <Form.Item
                label={<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><TagsOutlined /><span>Keywords</span></div>}
                style={{ marginBottom: 0 }}
              >
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => setKeywordsModalVisible(true)} style={{ width: '100%' }} size="large">
                  {keywords.length ? `Keywords (${keywords.length})` : 'Add Keywords'}
                </Button>
              </Form.Item>
            </div>

            <div style={{ flex: 1 }}>
              <Form.Item label="CTAs" style={{ marginBottom: 0 }}>
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => setCtasModalVisible(true)} style={{ width: '100%' }} size="large">
                  {ctas.length ? `CTAs (${ctas.length})` : 'Add CTAs'}
                </Button>
              </Form.Item>
            </div>
          </div>


          {/* Basic Info */}
          <Form.Item
            label={<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><InfoCircleOutlined /><span>Basic Info</span></div>}
            name="basicInfo"
          >
            <TextArea
              rows={4}
              placeholder="Write basic info about your business, product, or service. This replaces {basic_info} in your prompts."
            />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '-16px', marginBottom: '16px' }}>
            💡 This replaces <code style={{ background: '#f0f0f0', padding: '0 3px', borderRadius: '3px' }}>{'{basic_info}'}</code> in your prompt automatically.
          </Typography.Text>

          {/* ── NEW: Brand Voice ───────────────────────────────────────────── */}
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <SoundOutlined />
                <span>Brand Voice &amp; Writing Style</span>
                <Typography.Text type="secondary" style={{ fontWeight: 'normal', fontSize: '12px' }}>(Optional)</Typography.Text>
              </div>
            }
            name="brandVoice"
          >
            <TextArea
              rows={4}
              maxLength={2000}
              showCount
              placeholder="Paste 2-3 paragraphs of your own writing, or describe your style — e.g. casual, short sentences, first person, slightly humorous"
            />
          </Form.Item>
    
          {/* ── END Brand Voice ────────────────────────────────────────────── */}

          {/* Info pointing to Prompt Library */}
          <div style={{ padding: '10px 14px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', fontSize: '12px', color: '#5b21b6', marginBottom: '20px' }}>
            💡 <strong>Prompts are in Prompt Library.</strong>{' '}
            Go to <strong>Prompt Library</strong> in the sidebar to create reusable prompts, then select them when generating AI answers.
          </div>

          {/* Submit Buttons */}
          <Form.Item style={{ marginBottom: 0 }}>
            <Row gutter={12}>
              <Col><Button onClick={handleCancel}>Cancel</Button></Col>
              <Col>
                <Button type="primary" htmlType="submit" loading={loading} disabled={keywords.length === 0 || ctas.length === 0}>
                  {isEditMode ? 'Update Angle' : 'Add Angle'}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* Keywords Modal */}
      <Modal title="Add Keywords" visible={keywordsModalVisible} onCancel={() => setKeywordsModalVisible(false)} onOk={handleKeywordsSubmit} okText="Add Keywords" width={500}>
        <div style={{ marginBottom: '12px' }}>
          <Typography.Text type="secondary">Enter one keyword per line:</Typography.Text>
        </div>
        <TextArea rows={8} value={keywordsText} onChange={(e) => setKeywordsText(e.target.value)} placeholder={`keyword 1\nkeyword 2\nkeyword 3`} />
      </Modal>

      {/* CTAs Modal */}
      <Modal title="Add CTAs" visible={ctasModalVisible} onCancel={() => setCtasModalVisible(false)} onOk={handleCtasSubmit} okText="Add CTAs" width={600}>
        <Space direction="vertical" style={{ width: '100%', top: 100 }}>
          {ctaInputs.map((cta, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px' }}>
              <Input placeholder={`CTA ${index + 1}`} value={cta} onChange={(e) => updateCtaInput(index, e.target.value)} style={{ flex: 1 }} />
              {ctaInputs.length > 1 && (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeCtaInput(index)} />
              )}
            </div>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} onClick={addCtaInput} style={{ width: '100%' }}>Add More CTA</Button>
        </Space>
      </Modal>
    </>
  );
};

export default AngleForm;